import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { optOutBookingPostHog } from '../analytics';

type ConsentStatus = 'granted' | 'denied' | 'unknown';

interface ConsentContextValue {
  consent: ConsentStatus;
  isDecided: boolean;
  grantConsent: () => void;
  denyConsent: () => void;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

export const CONSENT_STORAGE_KEY = 'analytics_consent';

const readStoredConsent = (): ConsentStatus => {
  try {
    const saved = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (saved === 'granted' || saved === 'denied') {
      return saved;
    }
  } catch {
    // ignore storage errors
  }
  return 'unknown';
};

/** App Store 5.1.2(i) — pas de cookies / tracking analytics sur iOS natif (miroir Pro). */
export function isNativeIosPlatform(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isNativeIOS = isNativeIosPlatform();
  const [consent, setConsent] = useState<ConsentStatus>(() =>
    isNativeIOS ? 'denied' : readStoredConsent()
  );

  useEffect(() => {
    if (isNativeIOS) {
      setConsent('denied');
      optOutBookingPostHog();
      return;
    }

    const saved = readStoredConsent();
    if (saved !== 'unknown') {
      setConsent(saved);
    }
  }, [isNativeIOS]);

  const saveConsent = useCallback(
    (status: Exclude<ConsentStatus, 'unknown'>) => {
      if (isNativeIOS) {
        setConsent('denied');
        return;
      }

      setConsent(status);
      try {
        window.localStorage.setItem(CONSENT_STORAGE_KEY, status);
      } catch {
        // ignore storage errors
      }
    },
    [isNativeIOS]
  );

  const grantConsent = useCallback(() => saveConsent('granted'), [saveConsent]);
  const denyConsent = useCallback(() => saveConsent('denied'), [saveConsent]);

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      isDecided: consent !== 'unknown',
      grantConsent,
      denyConsent,
    }),
    [consent, grantConsent, denyConsent]
  );

  useEffect(() => {
    if (consent === 'denied') {
      optOutBookingPostHog();
    }
  }, [consent]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return ctx;
};
